"use client"

import React from "react";

import Image from "next/image";

import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {CTAButton} from "@/shadcn/ui/button";
import {motion} from "framer-motion";

import {PageIQCapitalLaunchSchema} from "@/sanity/pages/PageIQCapitalLaunchTemplate";

import dots from "@/public/iq-capital-launch/dots-top.svg";
import close from "@/public/iq-capital-launch/close.svg";
import dotArrow from "@/public/iq-capital-launch/dotarrow-iq.svg";
import dotArrowDown from "@/public/iq-capital-launch/dotarrow-down.svg";

const getPortableTextLength = (blocks: any): number => {
  if (!blocks || !Array.isArray(blocks)) return 0;

  return blocks.reduce((total, block) => {
    if (block._type === 'block' && block.children) {
      return total + block.children.reduce((sum: number, child: any) => {
        return sum + (child.text || '').length;
      }, 0);
    }
    return total;
  }, 0);
};

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const fadeInDelay = (delay: number) => ({
  ...fadeIn,
  transition: { ...fadeIn.transition, delay }
});

const fadeInStagger = (index: number, baseDelay = 0.1) => ({
  ...fadeIn,
  transition: { ...fadeIn.transition, delay: baseDelay + (index * 0.1) }
});

export default function IQCapitalSection({
  data,
  className
}: {
  data: PageIQCapitalLaunchSchema;
  className?: string;
}) {
  return (
    <section
      id="IQCapital"
      className={cn(
        "relative pb-[8rem] lg:pb-[16rem] px-[2rem]",
        className
      )}
    >
      <div className="w-[min(226rem,100%)] py-[8rem] mx-auto relative overflow-clip bg-white/[.03] rounded-[3rem] lg:rounded-[4rem]">
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(97,91,80,.17)] from-[-80%] via-[rgba(199,187,164,.17)] via-[6%] to-[rgba(97,91,80,.17)] to-[92%] pointer-events-none" />
        <Image
          className="absolute top-0 left-0 w-[max(125rem,100%)] !max-w-[unset] pointer-events-none"
          src={dots}
          alt=""
        />

        <div className="container sm:px-[4rem] flex flex-col items-center relative">
          {data?.iqcapitalIcon && (
            <motion.div {...fadeInDelay}>
              <Image
                className="!w-[7rem] mb-[2rem]"
                src={data.iqcapitalIcon}
                alt=""
              />
            </motion.div>
          )}

          {data?.iqcapitalTitle && (
            <motion.div
              {...fadeInDelay(0.1)}
              className="richtext prose-h2:w-[30rem] lg:prose-h2:w-[min(95.5rem,100%)] prose-p:text-white/60 prose-p:w-[min(76.5rem,100%)] lg:prose-p:text-[2.25rem] prose-p:mb-[1rem] mb-[2rem]"
            >
              <PortableText
                value={data.iqcapitalTitle}
                components={richTextComponent}
              />
            </motion.div>
          )}

          {data?.iqcapitalChips && (
            <div className="flex flex-col items-center lg:flex-row lg:justify-center gap-[.5rem]">
              {data.iqcapitalChips.map((chip, index) => (
                <motion.div
                  key={index}
                  {...fadeInStagger(index, 0.1)}
                  className="relative gradient-border-white-2 before:rotate-180 before:opacity-10 bg-white/[.06] rounded-full flex items-center gap-[1rem] pl-[.75rem] py-[.75rem] pr-[2rem]"
                >
                  <div className="w-[5rem] h-[3.375rem] relative gradient-border-white-2 before:rotate-180 before:opacity-20 bg-white/10 rounded-full flex items-center justify-center">
                    <Image
                      className="!w-[2rem]"
                      src={close}
                      alt="close icon"
                    />
                  </div>

                  {chip?.text && (
                    <div className="richtext prose-p:text-white">
                      <PortableText
                        value={chip.text}
                        components={richTextComponent}
                      />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}

          <motion.hr
            {...fadeInDelay(0.1)}
            className="border-t border-white/10 w-[min(137.25rem,100%)] my-[3rem] hidden lg:block"
          />

          <div className="w-[min(137.25rem,100%)] flex flex-col items-center lg:flex-row">
            <div className="relative w-full h-[36rem] lg:h-full translate-x-[2.5rem] lg:translate-x-0">
              <Image
                className="!w-[72rem] lg:!w-[111.75rem] !max-w-[unset] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                src={data.iqcapitalImage}
                alt=""
              />
            </div>

            <div className="flex-shrink-0 w-[min(63.25rem,100%)] flex flex-col items-center lg:items-start relative">
              {data?.iqcapitalText && (
                <motion.div
                  {...fadeInDelay(0.1)}
                  className="richtext lg:richtext-start prose-p:text-white/60 lg:prose-p:text-[2.25rem] mb-[2rem]"
                >
                  <PortableText
                    value={data.iqcapitalText}
                    components={richTextComponent}
                  />
                </motion.div>
              )}

              <motion.div
                {...fadeInDelay(0.1)}
                className="w-full flex gap-[1rem] justify-start items-center mb-[1.5rem]"
              >
                <Image
                  className="!w-[4rem] flex-shrink-0"
                  src={dotArrow}
                  alt="arrow"
                />
                <h3 className="text-white font-normal text-[2.25rem] mb-0">{data?.iqcapitalListTitle}</h3>
              </motion.div>

              {data?.iqcapitalList && (
                <div className="w-full flex flex-col gap-[.5rem]">
                  {data?.iqcapitalList.map((item, index) => (
                    <motion.div
                      key={index}
                      {...fadeInStagger(index, 0.1)}
                      className="relative border border-white/10 bg-white/[.06] rounded-[1rem] lg:rounded-[2rem] p-[.5rem] backdrop-blur-[5px]"
                    >
                      <div className={`border border-white/10 bg-white/[.01] rounded-[.675rem] lg:rounded-[1.5rem] p-[2rem] flex gap-[2rem] ${getPortableTextLength(item?.text) > 70 ? 'items-start' : 'items-center'}`}>
                        <div className="flex-shrink-0 w-[4rem] h-[4rem] bg-gradient-to-b from-[#615B50] from-[-148%] via-[#C7BBA4] via-[-28%] to-[#615B50] to-[92%] rounded-[.75rem] flex items-center justify-center">
                          <div className="w-[3.5rem] h-[3.5rem] rounded-[.625rem] border border-white/30 flex items-center justify-center">
                            <p className="mb-0 text-white font-medium text-[1.75rem] leading-none">
                              {(index + 1).toString().padStart(2, "0")}
                            </p>
                          </div>
                        </div>

                        {item?.text && (
                          <div className="richtext richtext-start prose-p:text-white/80">
                            <PortableText
                              value={item.text}
                              components={richTextComponent}
                            />
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <motion.div
            {...fadeInDelay(0.1)}
            className="w-[min(137.25rem,100%)] flex items-center gap-[2rem] my-[3rem]"
          >
            <div className="bg-gradient-to-l from-white grow h-[1px]" />
            <Image
              className="!w-[3.75rem] flex-shrink-0"
              src={dotArrowDown}
              alt="arrow down"
            />
            <div className="bg-gradient-to-r from-white grow h-[1px]" />
          </motion.div>

          <motion.div
            {...fadeInDelay(0.1)}
            className="w-[min(63.25rem,100%)] lg:w-[min(137.25rem,100%)] rounded-[3rem] border border-white/10 p-[2rem] lg:p-[1rem] grid grid-cols-1 lg:grid-cols-2 gap-[2rem] lg:gap-0"
          >
            <div className="lg:order-2 flex items-center justify-center">
              {data?.iqcapitalCTAText2 && (
                <div className="richtext lg:richtext-start w-[min(59.125rem,100%)] px-[1rem] sm:px-[2rem] prose-p:text-white/60 prose-p:text-[2.25rem]">
                  <PortableText
                    value={data.iqcapitalCTAText2}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>

            <div className="lg:order-1 relative border border-white/10 bg-white/[.06] rounded-[2rem] p-[.5rem]">
              <div className="h-full border border-white/10 bg-white/[.01] rounded-[1.5rem] p-[2rem] flex flex-col items-start">
                {data?.iqcapitalCTAIcon && (
                  <Image
                    className="!w-[6rem] flex-shrink-0 mb-[3rem]"
                    src={data.iqcapitalCTAIcon}
                    alt="arrow down"
                  />
                )}

                {data?.iqcapitalCTAText && (
                  <div className="richtext richtext-start prose-p:text-[2.25rem] prose-strong:text-white prose-h3:font-medium prose-h3:text-[2.25rem] prose-h3:mb-[.5rem] mb-[3rem]">
                    <PortableText
                      value={data.iqcapitalCTAText}
                      components={richTextComponent}
                    />
                  </div>
                )}

                {data?.iqcapitalCTA && (
                  <CTAButton
                    data={data.iqcapitalCTA}
                  />
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
